/**
 * NewsTableModal Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive modal for displaying news articles with:
 * - Card and table view modes with seamless switching
 * - Advanced filtering by category and search query
 * - Pagination with customizable page sizes
 * - Real-time filter status and applied filter tags
 * - Date parsing and sorting functionality (portalPublishDate)
 * - Responsive design for optimal viewing experience
 * - Keyboard and mouse interaction support
 * - Weekly category filtering system
 * 
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onClose - Callback function when modal is closed
 * @param {Array} data - Array of news article data to display
 * @param {string} title - Title for the modal header (default: "News Hub")
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { useNavigate, useLocation } from 'react-router-dom';
import ResearchCenterTable from '../components/NewsRoom/ResearchCenterTable';
import ResearchCentreCards from '../components/NewsRoom/ResearchCentreCards';
import { formatDate } from '../utils/textUtils';
import apiHelper from '../utils/apiHelper';
import { useProfile } from '../contexts/ProfileContext';
import './NewsTableModal.css';

/**
 * NewsTableModal component for displaying comprehensive news article listings
 * with advanced filtering and view mode capabilities
 */
const NewsTableModal = ({ show, onClose, data, title = "News Hub" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useProfile();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [loadingPageNumber, setLoadingPageNumber] = useState(1);

  // Parse date - adapted for news data structure (portalPublishDate)
  const parseDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Handle ISO string format
      if (dateString.includes('T')) {
        const parsed = new Date(dateString);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      
      // Handle YYYY-MM-DD format
      if (dateString.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
        const parsed = new Date(dateString + 'T00:00:00.000Z');
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      
      // Handle DD/MM/YYYY format
      if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const parts = dateString.split('/');
        if (parts[0] <= 31 && parts[1] <= 12) {
          const [day, month, year] = parts;
          const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
          if (!isNaN(parsed.getTime())) return parsed;
        }
      }
      
      const parsed = new Date(dateString);
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch (error) {
      return null;
    }
  };



  // Get all available categories
  const getAllCategories = () => {
    if (!data) return [];
    
    return [...new Set(data.map(newsItem => newsItem.weeklyCategory))]
      .filter(category => category && category.trim() !== '')
      .sort();
  };

  const [availableCategories, setAvailableCategories] = useState([]);

  // Update available categories when data changes
  useEffect(() => {
    const categories = getAllCategories();
    setAvailableCategories(categories);
  }, [data]);

  const uniqueCategories = availableCategories;

  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'category') {
      setSelectedCategory(value);
    }
    
    setCurrentPage(1);
  };

  // All filtered data
  const allFilteredData = useMemo(() => {
    const adaptedData = data.map(newsItem => ({
      ...newsItem,
      // Map portalPublishDate to issueDate for compatibility with ResearchCentre components
      issueDate: newsItem.portalPublishDate ? formatDate(newsItem.portalPublishDate) : '',
      displaySummary: newsItem.summary || newsItem.plainDescription || '',
      // Set sourceType so ResearchCentre components can show proper tags
      sourceType: 'news' // NewsTableModal shows news articles, so always 'news'
    }));

    const sortedAndFilteredData = adaptedData
      .filter(newsItem => {
        // Category filter
        const category = newsItem.weeklyCategory ? newsItem.weeklyCategory.trim().toLowerCase() : '';
        const matchesCategory = selectedCategory ? category === selectedCategory.toLowerCase() : true;

        // Search filter
        const matchesSearchQuery = searchQuery.trim()
          ? (() => {
              const query = searchQuery.trim().toLowerCase();
              const titleMatch = (newsItem.title || '').toLowerCase().includes(query);
              const summaryMatch = (newsItem.displaySummary || '').toLowerCase().includes(query);
              const weeklyMatch = (newsItem.weeklyCategory || '').toLowerCase().includes(query);
              
              return titleMatch || summaryMatch || weeklyMatch;
            })()
          : true;

        return matchesCategory && matchesSearchQuery;
      })
      .sort((a, b) => {
        const dateA = parseDate(a.portalPublishDate);
        const dateB = parseDate(b.portalPublishDate);
        return dateB - dateA; // Sort in descending order
      });

    return sortedAndFilteredData;
  }, [data, selectedCategory, searchQuery]);

  // Pagination calculations
  const paginationInfo = useMemo(() => {
    const totalCount = allFilteredData.length;
    const totalPagesCount = Math.ceil(totalCount / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageData = allFilteredData.slice(startIndex, endIndex);
    
    return {
      totalItems: totalCount,
      totalPages: totalPagesCount,
      currentPageData: pageData,
      startIndex,
      endIndex
    };
  }, [allFilteredData, currentPage, pageSize]);

  // Update pagination state when calculations change
  useMemo(() => {
    setTotalItems(paginationInfo.totalItems);
    setTotalPages(paginationInfo.totalPages);
  }, [paginationInfo.totalItems, paginationInfo.totalPages]);

  // Current page data for display
  const filteredData = paginationInfo.currentPageData;

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setLoadingPageNumber(newPage);
      setCurrentPage(newPage);
      setIsLoadingPage(true);
      
      setTimeout(() => {
        setIsLoadingPage(false);
      }, 300);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    setLoadingPageNumber(1);
  };



  const resetFilters = () => {
    setSelectedCategory('');
    setSearchQuery('');
    setCurrentPage(1);
    setPageSize(6);
  };

  const isFilterActive = () =>
    selectedCategory ||
    searchQuery !== '' ||
    pageSize !== (activeTabIndex === 0 ? 6 : 10);

  // Get applied filters for display
  const getAppliedFilters = () => {
    const appliedFilters = [];
    
    if (searchQuery) {
      appliedFilters.push({
        type: 'search',
        label: 'Search',
        value: searchQuery,
        onRemove: () => {
          setSearchQuery('');
          setCurrentPage(1);
        }
      });
    }
    
    if (selectedCategory) {
      appliedFilters.push({
        type: 'category',
        label: 'Category',
        value: selectedCategory,
        onRemove: () => {
          setSelectedCategory('');
          setCurrentPage(1);
        }
      });
    }
    
    return appliedFilters;
  };

  // Handle tab change
  const handleTabChange = (index) => {
    setActiveTabIndex(index);
    if (index === 1) {
      setPageSize(10);
    } else {
      setPageSize(6);
    }
    setCurrentPage(1);
  };

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      resetFilters();
    }
  }, [show]);

  useEffect(() => {
    if (show && profile?.email) {
      apiHelper.logModalOpen(profile.email, 'NewsTableModal', title, 'NewsTable');
    }
  }, [show, profile?.email]);

  if (!show) return null;

  return (
    <div className="news-table-modal-overlay" onClick={onClose}>
      <div className="news-table-modal" onClick={(e) => e.stopPropagation()}>
        <div className="news-table-modal-header">
          <div className="header-content">
            <h2>{title}</h2>
          </div>
          <div className="modal-subtitle-container">
            <p className="modal-subtitle">
              Check out the News Hub for all news.
            </p>
            <button 
              className="news-hub-link-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
                
                // Check if we're already on the NewsRoom page
                if (location.pathname === '/NewsRoom') {
                  // Already on NewsRoom, just scroll to research section
                  setTimeout(() => {
                    const newsHubElement = document.getElementById('research');
                    if (newsHubElement) {
                      newsHubElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                } else {
                  // Navigate to NewsRoom page first, then scroll
                  navigate('/NewsRoom');
                  setTimeout(() => {
                    const newsHubElement = document.getElementById('research');
                    if (newsHubElement) {
                      newsHubElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 500); // Longer delay to allow page to load
                }
              }}
            >
              Go to News Hub
            </button>
          </div>
          <button className="news-table-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="news-table-modal-body">
          <div id="researchCentre">
            <Tabs selectedIndex={activeTabIndex} onSelect={handleTabChange}>
              {/* Filters section */}
              <div className="filters-section">
                <div className="filters-bar">
                  <div className="search-and-controls-row">
                    <div className="research-search-box">
                      <i className="fas fa-search"></i>
                      <input
                        type="text"
                        placeholder="Search content..."
                        value={searchQuery}
                        onChange={e => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                      {searchQuery && (
                        <i 
                          className="fas fa-times clear-search" 
                          onClick={() => {
                            setSearchQuery('');
                            setCurrentPage(1);
                          }}
                        />
                      )}
                    </div>

                    <div className="inline-filter-controls">
                      {/* Category Filter */}
                      <div className="filter-with-clear">
                        <select value={selectedCategory} onChange={(e) => handleFilterChange('category', e.target.value)}>
                          <option value="">🏷️ All Categories</option>
                          {uniqueCategories.map(category => <option key={category} value={category}>{category}</option>)}
                        </select>
                        {selectedCategory && (
                          <button 
                            className="filter-clear-btn"
                            onClick={() => handleFilterChange('category', '')}
                            title="Clear category filter"
                          >
                            ×
                          </button>
                        )}
                      </div>

                      <div className="filter-with-clear page-size-filter">
                        <select
                          value={pageSize}
                          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        >
                          {activeTabIndex === 0 ? (
                            <>
                              <option value={6}>📄 6 per page</option>
                              <option value={12}>12 per page</option>
                              <option value={18}>18 per page</option>
                              <option value={36}>36 per page</option>
                            </>
                          ) : (
                            <>
                              <option value={10}>📄 10 per page</option>
                              <option value={20}>20 per page</option>
                              <option value={30}>30 per page</option>
                              <option value={50}>50 per page</option>
                            </>
                          )}
                        </select>
                      </div>

                      {isFilterActive() && (
                        <i className="fa-solid fa-rotate-left resetIcon" title='Reset Filters' onClick={resetFilters}></i>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Applied Filters & Stats Panel */}
              <div className="research-filters-status">
                <div className="applied-filters-section">
                  {getAppliedFilters().length > 0 && (
                    <div className="applied-filters">
                      <span className="applied-filters-label">Applied filters:</span>
                      <div className="filter-tags">
                        {getAppliedFilters().map((filter, index) => (
                          <div key={`${filter.type}-${index}`} className="filter-tag">
                            <span className="filter-tag-label">{filter.label}:</span>
                            <span className="filter-tag-value">{filter.value}</span>
                            <button 
                              className="filter-tag-remove"
                              onClick={filter.onRemove}
                              title={`Remove ${filter.label} filter`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="instant-stats-panel">
                  <div className="stats-info">
                    <div className="stats-description">
                      <i className="fa-solid fa-chart-simple"></i>
                      {totalItems === 1 ? '1 item' : `${totalItems} items`} 
                    </div>
                  </div>
                  
                  <div className="view-controls">
                    <button 
                      className={`view-btn ${activeTabIndex === 0 ? 'active' : ''}`}
                      onClick={() => handleTabChange(0)}
                      title="Card View"
                    >
                      <i className="fas fa-th"></i>
                    </button>
                    <button 
                      className={`view-btn ${activeTabIndex === 1 ? 'active' : ''}`}
                      onClick={() => handleTabChange(1)}
                      title="Table View"
                    >
                      <i className="fas fa-list"></i>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Hidden TabList for react-tabs functionality */}
              <TabList style={{ display: 'none' }}>
                <Tab>Card View</Tab>
                <Tab>Table View</Tab>
              </TabList>
              
              <TabPanel>
                {isLoadingPage && (
                  <div className="page-loading">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading page {loadingPageNumber}...</span>
                  </div>
                )}
                <ResearchCentreCards data={filteredData} visibleCount={pageSize} searchQuery={searchQuery} />
              </TabPanel>
              <TabPanel>
                {isLoadingPage && (
                  <div className="page-loading">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading page {loadingPageNumber}...</span>
                  </div>
                )}
                <ResearchCenterTable data={filteredData} visibleCount={pageSize} searchQuery={searchQuery} />
              </TabPanel>
              
              {/* Pagination */}
              {totalPages > 1 && !isLoadingPage && (
                <div className="pagination">
                  <div className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsTableModal; 